import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import HeroSectionV2 from "../../components/HeroSectionV2";
import Billede1 from "../../Images/Billede1.jpg";
import "./media.css";

interface Event {
    eventID: number;
    eventName: string;
}

interface FunctionDto {
    functionId: number;
    functionName: string;
    categoryName?: string;
}

interface ApplicationStatusDto {
    id: number;
    status: "Pending" | "Approved" | "Denied";
    functionId: number;
    note: string;
    eventId: number;
}

const MediaDashboard: React.FC = () => {
    const { eventId } = useParams();
    const apiUrl = process.env.REACT_APP_API_URL as string;

    const userId = Number(sessionStorage.getItem("userId"));
    const username = sessionStorage.getItem("username");
    const email = sessionStorage.getItem("userEmail");

    const [events, setEvents] = useState<Event[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
    const [functions, setFunctions] = useState<FunctionDto[]>([]);
    const [selectedFunction, setSelectedFunction] = useState<number | null>(null);
    const [note, setNote] = useState("");
    const [applications, setApplications] = useState<ApplicationStatusDto[]>([]);
    const [message, setMessage] = useState("");

    useEffect(() => {
        fetch(`${apiUrl}/api/events`)
            .then(res => res.json())
            .then(setEvents)
            .catch(console.error);
    }, []);

    useEffect(() => {
        if (eventId) setSelectedEventId(Number(eventId));
    }, [eventId]);

    const loadApplications = async (eventIdValue: number) => {
        const appRes = await fetch(
            `${apiUrl}/api/media-applications/${eventIdValue}/my-application?userId=${userId}`
        );

        const appData = appRes.ok ? await appRes.json() : [];
        setApplications(Array.isArray(appData) ? appData : []);
    };

    useEffect(() => {
        if (!selectedEventId || !userId) return;

        const loadData = async () => {
            try {
                const res = await fetch(`${apiUrl}/api/events/${selectedEventId}/media-data`);
                const data = res.ok ? await res.json() : { functions: [] };
                setFunctions(
                    data.functions
                        .filter((f: any) =>
                            f.categoryName?.trim().toLowerCase() === "media"
                        )
                        .map((f: any) => ({
                            functionId: f.functionId,
                            functionName: f.functionName?.trim(),
                            categoryName: f.categoryName?.trim()
                        }))
                );

                await loadApplications(selectedEventId);
            } catch (err) {
                console.error(err);
                setApplications([]);
            }
        };

        loadData();
    }, [selectedEventId, userId]);

    const handleApply = async () => {
        if (!selectedEventId || !selectedFunction) return;

        try {
            const res = await fetch(`${apiUrl}/api/media-applications/${selectedEventId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    userId,
                    functionId: selectedFunction,
                    note,
                }),
            });

            if (!res.ok) {
                setMessage("Could not submit application. Please try again.");
                return;
            }

            await loadApplications(selectedEventId);

            setMessage("Your application has been submitted!");
            setNote("");
            setSelectedFunction(null);
        } catch (err) {
            console.error(err);
            setMessage("Failed to submit application. Please try again.");
        }
    };

    const counts = {
        pending: applications.filter(a => a.status === "Pending").length,
        approved: applications.filter(a => a.status === "Approved").length,
        denied: applications.filter(a => a.status === "Denied").length,
    };

    const activeApplication = applications
        .filter(a => a.status === "Pending" || a.status === "Approved")
        .sort((a, b) => b.id - a.id)[0];

    const inputsDisabled = !!activeApplication;

    const statusMessage =
        activeApplication?.status === "Pending"
            ? "Your application is pending."
            : activeApplication?.status === "Approved"
                ? "Your application has been approved."
                : "";

    const eventName =
        events.find(ev => ev.eventID === selectedEventId)?.eventName || "Unknown Event";

    return (
        <div>
            <HeroSectionV2
                titleEn="Media"
                titleDa="Media"
                descriptionEn="Application for media access"
                descriptionDa="Ansøgning til medieadgang"
                imageSrc={Billede1}
            />

            <div className="media-user-page">
                <div className="media-user-header">
                    <div>
                        <h1>Media Dashboard</h1>
                        <p>Apply for media access and follow your application status.</p>
                    </div>
                </div>

                <div className="media-user-info-card">
                    <div>
                        <span>Name</span>
                        <strong>{username || "-"}</strong>
                    </div>

                    <div>
                        <span>Email</span>
                        <strong>{email || "-"}</strong>
                    </div>

                    <div>
                        <span>Event</span>
                        <strong>{selectedEventId ? eventName : "No event selected"}</strong>
                    </div>
                </div>

                <div className="media-user-grid">
                    <div className="media-user-box">
                        <h2>Apply for Media Access</h2>
                        <p className="media-user-box-subtitle">
                            Choose the function you are applying for.
                        </p>

                        <label>Function</label>
                        <select
                            value={selectedFunction ?? ""}
                            onChange={e => setSelectedFunction(Number(e.target.value))}
                            disabled={inputsDisabled}
                        >
                            <option value="">Select Function</option>
                            {functions
                                .filter(f => f.categoryName?.trim().toLowerCase() === "media")
                                .map(f => (
                                    <option key={f.functionId} value={f.functionId}>
                                        {f.functionName}
                                    </option>
                                ))}
                        </select>

                        <label>Note</label>
                        <textarea
                            rows={5}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            disabled={inputsDisabled}
                            placeholder="Optional note..."
                        />

                        <button
                            disabled={!selectedFunction || inputsDisabled}
                            onClick={handleApply}
                            className="media-submit-btn"
                        >
                            {inputsDisabled ? "Application Active" : "Submit Application"}
                        </button>

                        {statusMessage && (
                            <div className="media-warning-message">
                                {statusMessage}
                            </div>
                        )}

                        {message && (
                            <div className="media-success-message">
                                {message}
                            </div>
                        )}
                    </div>

                    <div className="media-user-box">
                        <div className="media-user-box-top">
                            <div>
                                <h2>Your Applications</h2>
                                <p className="media-user-box-subtitle">
                                    Overview of your submitted applications.
                                </p>
                            </div>
                        </div>

                        <div className="media-user-status-row">
                            <span className="media-status media-status-Pending">
                                Pending: {counts.pending}
                            </span>
                            <span className="media-status media-status-Approved">
                                Approved: {counts.approved}
                            </span>
                            <span className="media-status media-status-Denied">
                                Denied: {counts.denied}
                            </span>
                        </div>

                        {!selectedEventId && (
                            <div className="media-empty">
                                No event selected.
                            </div>
                        )}

                        {selectedEventId && applications.length === 0 && (
                            <div className="media-empty">
                                No applications for this event yet.
                            </div>
                        )}

                        {applications.length > 0 && (
                            <div className="media-applications-list">
                                {applications.map(app => {
                                    const functionName =
                                        functions.find(f => f.functionId === app.functionId)?.functionName || "Unknown";

                                    return (
                                        <div
                                            key={app.id}
                                            className={`media-application-card media-application-${app.status}`}
                                        >
                                            <div className="media-application-card-header">
                                                <h3>{functionName}</h3>
                                                <span className={`media-status media-status-${app.status}`}>
                                                    {app.status}
                                                </span>
                                            </div>

                                            {app.note && (
                                                <div className="media-note-box">
                                                    <strong>Note</strong>
                                                    <p>{app.note}</p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MediaDashboard;